import { useRouterState } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { composeDocumentTitle } from "./compose-document-title";

interface MetaEntry {
	title?: string;
}

interface MatchWithMeta {
	meta?: Array<MetaEntry | undefined>;
}

/**
 * Returns the title key declared by the deepest matched route, mirroring the
 * router's own precedence (see headContentUtils.ts in @tanstack/react-router:
 * matches are walked last-to-first and the first title found wins).
 */
export function findDeepestRouteTitle(
	matches: Array<MatchWithMeta>
): string | undefined {
	for (let index = matches.length - 1; index >= 0; index--) {
		const meta = matches[index]?.meta;
		if (!meta) continue;

		for (let metaIndex = meta.length - 1; metaIndex >= 0; metaIndex--) {
			const title = meta[metaIndex]?.title;
			if (title) return title;
		}
	}

	return undefined;
}

/**
 * The composed browser-tab text for the current route.
 *
 * Uses `useTranslation()` rather than the module-level `t` from i18next that
 * .claude/rules/design-quality.md prescribes for user-facing text. That rule is
 * about leaf components rendering copy; here reactivity is the whole point.
 * Locale JSON is fetched over HTTP (i18next-http-backend), so a route can match
 * before its bundle lands — the hook re-renders when it arrives and when the
 * language changes, and the module-level `t` would not. Do not "fix" this.
 */
export function useDocumentTitleValue(): string {
	const { t } = useTranslation();

	const titleKey = useRouterState({
		select: (state) => findDeepestRouteTitle(state.matches),
	});

	return composeDocumentTitle(titleKey ? t(titleKey) : undefined);
}
